import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { fetchConfig } from "@/api";
import MouseParticles from "@/components/MouseParticles";
import LiveStats from "@/components/LiveStats";
import LatestReleaseCard from "@/components/LatestReleaseCard";
import PortfolioSection from "@/components/PortfolioSection";
import ProductsSection from "@/components/ProductsSection";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import PricingBlock from "@/components/PricingBlock";
import TosBlock from "@/components/TosBlock";
import { Terminal, MessageSquare, ArrowDown } from "lucide-react";

const FALLBACK_HERO_BG = "https://static.prod-images.emergentagent.com/jobs/805494e7-01c8-4c55-a6ba-1f3d21c3fc0a/images/4373ccddef1bffadd20e79704507b591c33895aecce314607af02fafced7d068.png";

// Returns 'video' if URL looks like a video file, otherwise 'image'
const detectHeroType = (url) => {
  if (!url) return "image";
  try {
    const clean = url.split("?")[0].split("#")[0].toLowerCase();
    if (/\.(mp4|webm|mov|m4v|ogv)$/.test(clean)) return "video";
  } catch (_) { /* ignore */ }
  return "image";
};

const MarqueeStrip = () => (
  <div
    data-testid="marquee-strip"
    className="relative bg-[var(--theme-surface)]/70 backdrop-blur-sm text-[var(--theme-text)] border-y border-[var(--theme-border)] overflow-hidden py-3.5"
  >
    <div className="marquee-track font-display text-base tracking-[0.22em] uppercase">
      {Array.from({ length: 2 }).map((_, k) => (
        <span key={k} className="inline-flex">
          {[
            "NULL // MODIFICATIONS",
            "3D VEHICLE BUILDS",
            "1-OF-1 EXCLUSIVES",
            "ESCROW PROTECTED",
            "NO RESALE",
            "BUILT FOR YOUR SERVER",
            "CUSTOM HANDLING",
            "DEBADGING AVAILABLE",
          ].map((t, i) => (
            <span key={i} className="px-8 flex items-center gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] inline-block shadow-[0_0_8px_rgba(var(--theme-accent-primary-rgb),0.8)]" />
              {t}
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

const SectionHeader = ({ tag, title, kicker }) => (
  <div className="mb-12 md:mb-16">
    <div className="bracket mb-3">{tag}</div>
    <h2
      data-text={title}
      className="glitch font-display text-4xl md:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight max-w-3xl"
    >
      {title}
    </h2>
    {kicker && (
      <p className="mt-4 text-[var(--muted)] max-w-2xl text-base leading-relaxed">{kicker}</p>
    )}
  </div>
);

export default function Landing() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [enableParticles, setEnableParticles] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Backward-compat: if a visitor lands on `/#pricing`, `/#products`,
  // `/#portfolio`, or `/#tos` (old hash-anchor style), route them to the new
  // dedicated pages. Handles both direct paste and initial-load scenarios.
  useEffect(() => {
    // If a share-link `?v=<slug>` is present, this was a backend redirect
    // targeting a section — let ProductsSection / PortfolioSection auto-open
    // the modal on this same page instead of navigating away.
    const params = new URLSearchParams(window.location.search);
    if (params.get("v")) return;

    const raw = (window.location.hash || "").toLowerCase().replace(/^#/, "");
    const map = {
      pricing: "/pricing",
      products: "/products",
      shop: "/products",
      portfolio: "/portfolio",
      tos: "/tos",
    };
    if (raw && map[raw]) {
      // Preserve any query string; drop the hash.
      const qs = window.location.search || "";
      navigate(map[raw] + qs, { replace: true });
    }
    // Run once on mount — the browser only fires this hash on the first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchConfig()
      .then(setConfig)
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));

    // Only enable particles on non-touch / wider screens
    const okSize = window.matchMedia("(min-width: 768px)").matches;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setEnableParticles(okSize && !reducedMotion);
  }, []);

  // Surface CFX login result toasts (?cfx_login=1 or ?cfx_error=...)
  useEffect(() => {
    if (searchParams.get("cfx_login") === "1") {
      toast.success("CFX login successful");
      const next = new URLSearchParams(searchParams);
      next.delete("cfx_login");
      setSearchParams(next, { replace: true });
    } else if (searchParams.get("cfx_error")) {
      toast.error("CFX login failed: " + searchParams.get("cfx_error"));
      const next = new URLSearchParams(searchParams);
      next.delete("cfx_error");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--theme-bg)] px-6">
        <div className="flex flex-col items-center gap-4 text-center" data-testid="landing-loader">
          <img src="/logo.png?v=4" alt="" className="h-10 w-10 object-contain opacity-70" />
          <div className="font-mono text-[10px] tracking-[0.4em] text-[var(--theme-text-muted)] uppercase">
            Loading
          </div>
        </div>
      </div>
    );
  }

  if (err || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="font-mono text-red-500 text-sm">! ERROR: {err}</div>
      </div>
    );
  }

  const { tiers, settings } = config;
  const tebex = settings.tebex_url;
  const discord = settings.discord_url;
  const heroBg = settings.hero_bg || FALLBACK_HERO_BG;
  const heroType = detectHeroType(heroBg);
  const stats = settings.stats || [];

  return (
    <div className="min-h-screen bg-[var(--theme-bg)] text-[var(--theme-text)] relative">
      {/* Ambient particle layer — kept per brand directive */}
      {enableParticles && <MouseParticles count={1900} />}
      <div className="nm-vignette" />

      {/* All content sits above the backdrop layers */}
      <div className="relative z-10">
      {/* NAV (shared component — same instance on subpages) */}
      <SiteNav config={config} />

      {/* HERO */}
      <section
        id="top"
        data-testid="hero-section"
        className="relative min-h-screen flex items-stretch pt-16 overflow-hidden"
      >
        {heroType === "video" ? (
          <video
            key={heroBg}
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
            src={heroBg}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          />
        ) : (
          <div
            data-testid="hero-image"
            className="absolute inset-0 bg-cover bg-center opacity-45"
            style={{ backgroundImage: `url(${heroBg})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--theme-bg)] via-[var(--theme-bg)]/80 to-[var(--theme-bg)]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--theme-bg)] via-transparent to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-[0.12]" />

        {/* Side rail (left vertical text) */}
        <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 rail-tick pl-3 h-[60vh] flex-col justify-between items-center">
          <div className="v-text font-mono text-[10px] text-[var(--muted)] tracking-[0.4em] uppercase">
            NULL // PRICE_INDEX
          </div>
          <div className="v-text font-mono text-[10px] text-[var(--accent)] tracking-[0.4em] uppercase">
            ▲ ENC_OK / SYS_STABLE
          </div>
        </div>

        <div className="relative z-10 w-full grid lg:grid-cols-[1fr_auto] gap-10 max-w-[100rem] mx-auto px-6 md:px-16 py-20 lg:py-32 items-center">
          {/* LEFT: title */}
          <div className="lg:pl-12">
            <div className="bracket mb-6 flex items-center gap-3 flex-wrap">
              <span className="w-2 h-2 bg-[var(--accent)] animate-pulse" />
              SYSTEM_ONLINE // NullModifications
              <span className="inline-flex items-center justify-center text-[10px] font-bold text-black bg-[var(--accent)] px-1.5 py-[1px] tracking-normal ml-0.5">
                TM
              </span>
              // {new Date().getFullYear()}
            </div>

            <h1
              data-text="Null"
              className="font-display uppercase text-[22vw] lg:text-[12rem] xl:text-[14rem] leading-[0.82] tracking-[0.01em]"
            >
              Null
            </h1>
            <h1
              data-text="Modifications"
              className="font-display uppercase text-outline text-[11vw] lg:text-[6.5rem] xl:text-[7.5rem] leading-[0.9] tracking-[0.02em] mt-2"
            >
              Modifications
            </h1>

            {/* Accent separator */}
            <div className="mt-10 h-[3px] hazard max-w-md" />

            <p className="mt-8 text-base md:text-xl text-[var(--text)] max-w-2xl leading-relaxed font-light">
              {settings.tagline}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#pricing" className="btn-mega" data-testid="hero-cta-pricing">
                <Terminal size={14} /> VIEW PRICES
              </a>
              <a
                href={discord}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                data-testid="hero-cta-discord"
              >
                <MessageSquare size={14} /> COMMISSION A BUILD
              </a>
            </div>
          </div>

          {/* RIGHT: stat block — live stats (Discord + GTA5-Mods) override manual stats when enabled */}
          <div
            className={`hidden lg:flex flex-col gap-3 ${
              settings.live_stats?.enabled ? "w-[560px]" : "w-80"
            } self-start mt-16`}
          >
            {settings.live_stats?.enabled ? (
              <LiveStats />
            ) : (
              <div className="flex flex-col gap-3">
                {stats.map((s, i) => {
                  const isLast = i === stats.length - 1;
                  if (isLast) {
                    return (
                      <div key={s.id} className="bg-[var(--accent)] text-black px-5 py-4 rounded-2xl shadow-[0_0_30px_-8px_rgba(var(--theme-accent-primary-rgb),0.5)]">
                        <div className="font-mono text-[9px] tracking-[0.3em]">// {s.label}</div>
                        <div className="font-display text-3xl mt-1 flex items-center gap-2 tracking-wide">
                          <span className="w-2 h-2 rounded-full bg-black animate-pulse" />
                          {s.value}
                        </div>
                        {s.sublabel && (
                          <div className="font-mono text-[10px] uppercase mt-1">{s.sublabel}</div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={s.id} className="bg-[var(--theme-surface)]/80 backdrop-blur border border-[var(--theme-border)] px-5 py-4 rounded-2xl">
                      <div className="font-mono text-[9px] text-[var(--accent)] tracking-[0.3em]">// {s.label}</div>
                      <div className="font-display text-4xl text-white mt-1 tracking-wide">{s.value}</div>
                      {s.sublabel && (
                        <div className="font-mono text-[10px] text-[var(--muted)] uppercase">{s.sublabel}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Latest Release card (admin-curated, shown below stats block) */}
            <LatestReleaseCard release={settings.latest_release} />
          </div>
        </div>

        {/* Scroll indicator */}
        <a
          href="#pricing"
          className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 font-mono text-[10px] text-[var(--muted)] uppercase tracking-widest animate-bounce z-20"
        >
          <ArrowDown size={14} className="text-[var(--accent)]" />
          scroll
        </a>
      </section>

      <MarqueeStrip />

      {/* PRICING TIERS (shared block — same on /pricing) */}
      <PricingBlock tiers={tiers} />

      {/* PRODUCTS / STOREFRONT */}
      <ProductsSection categories={config.product_categories || []} />

      {/* PORTFOLIO */}
      <PortfolioSection portfolio={config.portfolio || []} />

      {/* TOS (shared block — same on /tos) */}
      <TosBlock tos={settings.tos || []} />

      {/* FOOTER (shared component — same on subpages) */}
      <SiteFooter config={config} />
      </div>
    </div>
  );
}
